import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Zap, ChevronRight, Clock, ShieldAlert, Activity, Layers3,
  TrendingDown, Sparkles,
} from "lucide-react";
import { api } from "../../lib/api";

/* ═══════════════════════════════════════════════════════════════════
   RecommendedNextActions — Phase 1 of the Mission Control Evolution.

   Renders the operational brain of the Deal Room: a calm grid of
   recommendation cards generated deterministically by the existing
   intervention engine + enriched with per-category narrative
   (momentum impact, risk-if-ignored, downstream effect, confidence
   impact, recovery value at stake, one-click action buttons).

   Visual identity unchanged: same cardStyle (rounded-2xl, slate-200
   border, soft shadow, white bg) as the existing intelligence rail.
   Calm 5-tone palette · subtle priority glow · no animation noise.

   Wires the one-click actions to POST /api/deals/{id}/actions/{key},
   which records a calm operational activity row on the timeline.
   ─────────────────────────────────────────────────────────────────── */

const URGENCY = {
  critical:      { fg: "#9F1239", bg: "rgba(244,63,94,0.10)",   dot: "#F43F5E", label: "Critical" },
  important:     { fg: "#92400E", bg: "rgba(245,158,11,0.12)",  dot: "#F59E0B", label: "Important" },
  informational: { fg: "#475569", bg: "rgba(148,163,184,0.14)", dot: "#94A3B8", label: "Informational" },
};

const CATEGORY_ICON = {
  slipping_exchange:   TrendingDown,
  completion_drift:    TrendingDown,
  document_blocker:    ShieldAlert,
  mortgage_risk:       ShieldAlert,
  exchange_inactivity: Activity,
  overdue_milestone:   Clock,
  fragile_chain:       Layers3,
  inactive_solicitor:  Clock,
  downstream_casualty: Layers3,
};

const ImpactPill = ({ label, value, tone = "slate", testid }) => {
  const toneStyles = {
    slate:  { fg: "#475569", bg: "rgba(148,163,184,0.10)" },
    amber:  { fg: "#92400E", bg: "rgba(245,158,11,0.10)" },
    rose:   { fg: "#9F1239", bg: "rgba(244,63,94,0.08)" },
    green:  { fg: "#047857", bg: "rgba(16,185,129,0.08)" },
  }[tone] || { fg: "#475569", bg: "rgba(148,163,184,0.10)" };
  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1 rounded-md"
      style={{ background: toneStyles.bg }}
      data-testid={testid}
    >
      <span
        className="text-[9px] uppercase tracking-[0.14em] font-bold"
        style={{ color: toneStyles.fg }}
      >
        {label}
      </span>
      <span
        className="text-[10.5px] font-semibold tabular-nums"
        style={{ color: toneStyles.fg }}
      >
        {value}
      </span>
    </div>
  );
};

const ActionCard = ({ item, dealId, onActionSuccess }) => {
  const [busyKey, setBusyKey] = useState(null);
  const cat = item.category;
  const Icon = CATEGORY_ICON[cat] || Sparkles;
  const u = URGENCY[item.urgency] || URGENCY.informational;
  const confPts = item.confidence_impact_pts;
  const recovery = item.recovery_value_gbp;

  const trigger = useCallback(async (actionKey) => {
    setBusyKey(actionKey);
    try {
      const { data } = await api.post(
        `/api/deals/${dealId}/actions/${actionKey}`,
        { intervention_id: item.id, note: null },
      );
      toast.success(data?.confirmation || "Action recorded on the timeline.");
      onActionSuccess?.(actionKey, data);
    } catch (err) {
      const msg = err?.response?.data?.detail?.message
                || err?.response?.data?.detail
                || "Could not trigger action.";
      toast.error(typeof msg === "string" ? msg : "Could not trigger action.");
    } finally {
      setBusyKey(null);
    }
  }, [dealId, item.id, onActionSuccess]);

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 transition-all"
      style={{
        borderLeft: `2px solid ${u.dot}`,
        transition: "all 240ms cubic-bezier(0.4,0,0.2,1)",
      }}
      data-testid={`recommended-action-card-${cat}`}
    >
      <div className="flex items-start gap-3 mb-2.5">
        <div
          className="w-8 h-8 rounded-lg shrink-0 inline-flex items-center justify-center"
          style={{ background: u.bg, color: u.fg }}
        >
          <Icon size={14} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] truncate"
               style={{ color: u.fg }}>
              {item.category_label}
            </p>
            <span
              className="text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 h-[16px] rounded-full inline-flex items-center shrink-0"
              style={{ background: u.bg, color: u.fg }}
            >
              {u.label}
            </span>
          </div>
          <p className="text-[13px] font-semibold leading-snug text-slate-900">
            {item.recommendation}
          </p>
        </div>
      </div>

      {/* Reasoning chips */}
      <div className="space-y-1.5 mb-3">
        {item.momentum_impact ? (
          <div className="flex items-start gap-1.5">
            <span
              aria-hidden
              className="inline-block w-1 h-1 rounded-full mt-1.5 shrink-0"
              style={{ background: "#94A3B8" }}
            />
            <p className="text-[11px] text-slate-600 leading-snug">
              <span className="font-semibold text-slate-700">Momentum impact · </span>
              {item.momentum_impact}
            </p>
          </div>
        ) : null}
        {item.risk_if_ignored ? (
          <div className="flex items-start gap-1.5">
            <span
              aria-hidden
              className="inline-block w-1 h-1 rounded-full mt-1.5 shrink-0"
              style={{ background: "#94A3B8" }}
            />
            <p className="text-[11px] text-slate-600 leading-snug">
              <span className="font-semibold text-slate-700">Risk if ignored · </span>
              {item.risk_if_ignored}
            </p>
          </div>
        ) : null}
        {item.downstream_effect ? (
          <div className="flex items-start gap-1.5">
            <span
              aria-hidden
              className="inline-block w-1 h-1 rounded-full mt-1.5 shrink-0"
              style={{ background: "#94A3B8" }}
            />
            <p className="text-[11px] text-slate-600 leading-snug">
              <span className="font-semibold text-slate-700">Downstream · </span>
              {item.downstream_effect}
            </p>
          </div>
        ) : null}
      </div>

      {/* Impact pills */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {confPts != null ? (
          <ImpactPill
            label="Confidence"
            value={confPts > 0 ? `+${confPts} pts` : `${confPts} pts`}
            tone={confPts <= -10 ? "rose" : confPts <= -5 ? "amber" : "slate"}
            testid={`recommended-action-conf-${cat}`}
          />
        ) : null}
        {recovery && recovery > 0 ? (
          <ImpactPill
            label="Recoverable"
            value={`£${recovery.toLocaleString()}`}
            tone={u.label === "Critical" ? "rose" : "amber"}
            testid={`recommended-action-recovery-${cat}`}
          />
        ) : null}
        {item.evidence?.length ? (
          <span className="text-[10.5px] text-slate-500 truncate min-w-0">
            {item.evidence[0]}
          </span>
        ) : null}
      </div>

      {/* One-click action buttons */}
      {item.actions?.length ? (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          {item.actions.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => trigger(a.key)}
              disabled={busyKey === a.key}
              className="inline-flex items-center gap-1 px-2.5 h-[28px] rounded-md text-[11px] font-semibold transition-all disabled:opacity-50"
              style={{
                background: a.primary
                  ? "linear-gradient(135deg, rgba(99,102,241,0.10), rgba(139,92,246,0.10))"
                  : "rgba(148,163,184,0.10)",
                color: a.primary ? "#4338CA" : "#475569",
                border: a.primary ? "1px solid rgba(99,102,241,0.22)" : "1px solid rgba(148,163,184,0.16)",
                transition: "all 200ms cubic-bezier(0.4,0,0.2,1)",
              }}
              data-testid={`recommended-action-btn-${cat}-${a.key}`}
            >
              <span>{a.label}</span>
              <ChevronRight size={11} strokeWidth={2.4} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const Skeleton = () => (
  <div
    className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 cf-pulse"
    data-testid="recommended-actions-loading"
  >
    <div className="h-3 w-32 bg-slate-100 rounded mb-2" />
    <div className="h-4 w-3/4 bg-slate-100 rounded mb-3" />
    <div className="h-2 w-full bg-slate-100 rounded mb-1.5" />
    <div className="h-2 w-2/3 bg-slate-100 rounded" />
  </div>
);

export const RecommendedNextActions = ({ dealId, onActionSuccess }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!dealId) return;
    let alive = true;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const { data } = await api.get(`/api/deals/${dealId}/intel-rail`);
        if (alive) setItems(data?.intel?.interventions || []);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [dealId, refreshTick]);

  const handleActionSuccess = useCallback((key, data) => {
    // Refresh interventions so the action ripple (new activity) lands
    // in the rest of the rail naturally next render.
    setRefreshTick((n) => n + 1);
    onActionSuccess?.(key, data);
  }, [onActionSuccess]);

  if (loading) {
    return (
      <section className="mb-5" data-testid="recommended-actions-section-loading">
        <Header count={null} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
          <Skeleton /><Skeleton />
        </div>
      </section>
    );
  }

  if (error || !items.length) return null;

  return (
    <section className="mb-5" data-testid="recommended-actions-section">
      <Header count={items.length} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
        {items.slice(0, 4).map((it) => (
          <ActionCard
            key={it.id}
            item={it}
            dealId={dealId}
            onActionSuccess={handleActionSuccess}
          />
        ))}
      </div>
    </section>
  );
};

const Header = ({ count }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <Zap size={12} className="text-indigo-600" strokeWidth={2.3} />
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-indigo-700">
        Recommended Next Actions
      </p>
    </div>
    {count != null ? (
      <span
        className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500"
        data-testid="recommended-actions-count"
      >
        {count} live
      </span>
    ) : null}
  </div>
);

export default RecommendedNextActions;
